import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { DateFacetFilter } from "@/components/filters/date-facet-filter";
import { ContactsFacetFilter } from "@/components/filters/contacts-facet-filter";
import { CollectionsFacetFilter } from "@/components/filters/collections-facet-filter";
import { NotesListFilter } from "@/modules/filters/notes/NotesListFilter";
import { MediaKindsFacetFilter } from "@/components/filters/media-kinds-facet-filter";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import {
  MDSText,
  MDSTextVariant,
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSTooltip,
  MDSPanelHeaderButtonGroup,
  MDSIconButton,
  MDSTooltipPlacement,
  MDSIconButtonSize,
  MDSIconKind,
  MDSFadeHorizontalScroll,
} from "@/mds";
import { ClearFiltersModal, useClearFiltersModal } from "@/components/filters/clear-filters-modal";
import { SortByDropdownButton } from "@/components/filters/sort-by-dropdown-button";

interface Props {}

export const ImportedNotesListPageHeader: FC<Props> = observer(() => {
  const page = useTabPageStore("notesListPage");

  const {
    filterButtonRef,
    showFilters,
    showFiltersModal,
    setShowFiltersModal,
    handleToggleFilters,
    handleClearFilters,
  } = useClearFiltersModal({
    clearAllFilters: page.filters.clearAllFilters,
    activeFiltersCount: page.filters.activeFiltersCount,
  });

  return (
    <>
      <Wrapper>
        <MDSPanelHeader>
          <MDSText
            slot={MDSPanelHeaderSlotName.Heading}
            variant={MDSTextVariant.HeaderXl}
            numberOfLines={1}
          >
            Imported Notes
          </MDSText>
          <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
            <MDSTooltip config={{ label: "Show filters", placement: MDSTooltipPlacement.Top }}>
              <MDSIconButton
                ref={filterButtonRef}
                iconKind={MDSIconKind.Filter}
                onClick={handleToggleFilters}
                isToggled={showFilters}
                size={MDSIconButtonSize.Medium}
              />
            </MDSTooltip>
          </MDSPanelHeaderButtonGroup>
        </MDSPanelHeader>
        {showFilters && (
          <WrapperBottom>
            <FiltersContainer>
              <SortByDropdownButton
                sortOptions={page.filters.sortOptions}
                defaultSortOption={NotesListFilter.DEFAULT_SORT_BY}
                selectedSortOption={page.filters.sortBy}
                onSelectSortOption={page.filters.setSortBy}
              />
              {renderSortedFilters(page.filters)}
            </FiltersContainer>
          </WrapperBottom>
        )}
      </Wrapper>
      <ClearFiltersModal
        triggerRef={filterButtonRef}
        isOpen={showFiltersModal}
        count={page.filters.activeFiltersCount}
        onCancel={() => setShowFiltersModal(false)}
        onConfirm={handleClearFilters}
      />
    </>
  );
});

ImportedNotesListPageHeader.displayName = "ImportedNotesListPageHeader";

const renderSortedFilters = (pageFilters: NotesListFilter) => {
  const filters = [
    {
      isActive:
        pageFilters.selectedCollectionTitles.length > 0 ||
        pageFilters.collectionNoCollectionIsSelected,
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          supportNoCollectionOption
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix={pageFilters.collectionNoCollectionIsSelected ? "" : "In"}
          buttonLabel="Collections"
          selectedModeKey={pageFilters.selectedCollectionModeKey}
          onSelectMode={pageFilters.onCollectionModeSelectionChange}
          noCollectionIsSelected={pageFilters.collectionNoCollectionIsSelected}
          onNoCollectionIsSelected={pageFilters.setCollectionNoCollectionIsSelected}
          selectedItems={pageFilters.selectedCollectionTitles}
        />
      ),
    },
    {
      isActive: !!pageFilters.mediaKindsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 1,
      component: (
        <MediaKindsFacetFilter
          key="media-kinds"
          items={pageFilters.mediaKindsFacetFilterItems}
          onSelectItem={pageFilters.toggleMediaKindItem}
          onSelectClear={pageFilters.clearMediaKinds}
          selectedItems={pageFilters.selectedMediaKinds}
        />
      ),
    },
    {
      isActive: !!pageFilters.params.filter?.date,
      defaultOrder: 2,
      component: (
        <DateFacetFilter
          key="date"
          items={pageFilters.dateFilterItems}
          selectedRange={pageFilters.dateSelectedRange}
          onClear={() => pageFilters.clearDateFilter()}
        />
      ),
    },

    {
      isActive: pageFilters.modifiedBySearchItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedBySearchItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdBySearchItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdBySearchItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div(() => ({}));

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing["4"],
}));

const FiltersContainer = styled(MDSFadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing["2"],
}));
